import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography, alpha, useTheme } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import menuService from '../../../../services/menuService';
import menuItemService from '../../../../services/menuItemService';
import type { Menu as MenuDoc, MenuItemKind, MenuType } from '../../../../types/menu';
import MenuItemFormGeneric from './MenuItemFormGeneric';

/**
 * MenuItem form dispatcher.
 *
 * Phase G.3 — every supported kind now renders the single generic item form
 * (MenuItemFormGeneric). The dispatcher only resolves the *intended* kind and
 * gates unsupported kinds; it no longer chooses between separate wine / non-wine
 * forms. "Wine" is an optional linked identity inside the generic form, not a
 * separate architecture.
 *
 * Resolution order:
 *   1. Edit mode (itemId present): load the item, read item.kind. Supported
 *      kinds render the generic form (seeded with the loaded kind); unsupported
 *      kinds (e.g. TASTING_MENU) render an explicit "not yet supported" message.
 *   2. Create mode (no itemId):
 *        a. `?kind=…` query param wins.
 *        b. Otherwise infer from menu.type:
 *             - FULL / BY_THE_GLASS / RESERVE / EVENT / PRIVATE → WINE
 *             - COCKTAIL → COCKTAIL, FOOD → DISH, etc.
 *             - MIXED / anything else → show a small kind picker.
 *
 * The generic form re-reads useParams itself; the dispatcher passes only the
 * intended kind, so route mounts (admin/venues/index.tsx) stay unchanged.
 */

const WINE_INFERRED_MENU_TYPES: MenuType[] = ['FULL', 'BY_THE_GLASS', 'RESERVE', 'EVENT', 'PRIVATE'];

// Phase 7.C.2b/7.C.3a — non-wine menu types infer their item kind. Note the
// menu-type vs item-kind renames: SPIRITS→SPIRIT, (TASTING→TASTING_MENU, still
// gated and intentionally absent here). All render the generic item form.
const MENU_TYPE_TO_ITEM_KIND: Partial<Record<MenuType, MenuItemKind>> = {
  COCKTAIL: 'COCKTAIL',
  FOOD: 'DISH',
  BEER: 'BEER',
  SPIRITS: 'SPIRIT',
  RETAIL: 'RETAIL',
  OTHER: 'OTHER',
};

// Kinds the dispatcher can route from a ?kind= query. All render the generic
// item form (MenuItemFormGeneric); WINE additionally activates the wine
// identity helper. TASTING_MENU stays gated.
const SUPPORTED_KINDS_PHASE_4_B: MenuItemKind[] = ['WINE', 'COCKTAIL', 'DISH', 'BEER', 'SPIRIT', 'RETAIL', 'OTHER'];
// Non-wine kinds routed straight to the generic form without the wine helper.
const NON_WINE_FORM_KINDS: MenuItemKind[] = ['COCKTAIL', 'DISH', 'BEER', 'SPIRIT', 'RETAIL', 'OTHER'];

// Phase G.4.c — universal add-item presets for the dispatcher's fallback picker
// (reached only on a no-?kind deep link to /add). Mirrors MenuDetail's
// ADD_ITEM_PRESETS: presets are helpers/defaults, not item architectures.
// TASTING_MENU is intentionally excluded (no authorable kind yet — pending
// choice-group/composition design).
const ADD_ITEM_PRESETS: { kind: MenuItemKind; label: string }[] = [
  { kind: 'WINE', label: 'Wine' },
  { kind: 'COCKTAIL', label: 'Cocktail' },
  { kind: 'DISH', label: 'Dish' },
  { kind: 'BEER', label: 'Beer' },
  { kind: 'SPIRIT', label: 'Spirit' },
  { kind: 'RETAIL', label: 'Retail' },
  { kind: 'OTHER', label: 'Other item' },
];

const MenuItemForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { menuId, itemId } = useParams<{ menuId: string; itemId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { venueId } = useVenue();
  const isEdit = Boolean(itemId);
  const backPath = `/admin/venues/${venueId}/menus/${menuId}`;

  const [menu, setMenu] = useState<MenuDoc | null>(null);
  const [itemKind, setItemKind] = useState<MenuItemKind | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // ── Load menu (+ item, in edit mode) in parallel ─────────────────────────
  useEffect(() => {
    if (!menuId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');

    const menuPromise = menuService.getMenu(venueId, menuId);
    const itemPromise = isEdit && itemId
      ? menuItemService.getItem(venueId, menuId, itemId)
      : Promise.resolve(null);

    Promise.all([menuPromise, itemPromise])
      .then(([m, item]) => {
        if (cancelled) return;
        setMenu(m);
        setItemKind(item ? item.kind : null);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(isEdit ? 'Could not load this item.' : 'Could not load this menu.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [venueId, menuId, isEdit, itemId]);

  // ── Loading / error shells ───────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !menu) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Menu
        </Button>
        <Alert severity="error">{loadError || 'Menu not found.'}</Alert>
      </Box>
    );
  }

  // ── Edit mode: render the generic form for any supported kind ────────────
  if (isEdit) {
    if (itemKind === 'WINE' || (itemKind && NON_WINE_FORM_KINDS.includes(itemKind))) {
      return <MenuItemFormGeneric intendedKind={itemKind} />;
    }
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Menu
        </Button>
        <Alert severity="warning">
          Editing items of kind <code>{itemKind ?? 'UNKNOWN'}</code> is not supported in this phase.
        </Alert>
      </Box>
    );
  }

  // ── Create mode: ?kind query → menu.type inference → kind picker ─────────
  const queryKindRaw = searchParams.get('kind');
  const queryKind = (queryKindRaw && SUPPORTED_KINDS_PHASE_4_B.includes(queryKindRaw as MenuItemKind))
    ? (queryKindRaw as MenuItemKind)
    : null;

  if (queryKind === 'WINE' || (queryKind && NON_WINE_FORM_KINDS.includes(queryKind))) {
    return <MenuItemFormGeneric intendedKind={queryKind} />;
  }

  // No ?kind — infer from menu type.
  if (WINE_INFERRED_MENU_TYPES.includes(menu.type)) return <MenuItemFormGeneric intendedKind="WINE" />;
  const inferredKind = MENU_TYPE_TO_ITEM_KIND[menu.type];
  if (inferredKind && NON_WINE_FORM_KINDS.includes(inferredKind)) {
    return <MenuItemFormGeneric intendedKind={inferredKind} />;
  }

  // Fallback picker — reached only when create mode has no resolvable kind
  // (no ?kind= and no menu-type suggestion, e.g. a MIXED menu or a direct
  // no-?kind deep link). Mirrors MenuDetail's universal add-item picker.
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Menu
          </Button>
          <Typography variant="h6" fontWeight={600}>Add item</Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            What are you adding?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Presets only set helpful defaults. Every item uses the same generic form.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {ADD_ITEM_PRESETS.map(opt => (
              <Button
                key={opt.kind}
                variant="contained"
                onClick={() => setSearchParams({ kind: opt.kind })}
                sx={{ textTransform: 'none', fontWeight: 600, minWidth: 140, flex: '1 1 140px' }}
              >
                {opt.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MenuItemForm;
